import { createSelector } from 'reselect';

import type APIAddress from '~/types/APIAddress';

import {
  type APIAddressHash,
  type AddressBookInstances,
  type AddressHash,
} from '~/reducers/address/address.reducer';
import { type SavedAPIAddress } from '~/types/APIAddress';
import { areAddressesFullyEqual } from '~/types/Address';

import { type ApplicationState } from '../store';

const getAddressStore = (state: ApplicationState) => state.address;

const getAddressKeyProp = (_: unknown, props: { addressKey: string }): string => props.addressKey;
const getAddressBookNameProp = (_: unknown, props: { addressBookName: string }) =>
  props.addressBookName;

export const getAddresses = createSelector(
  getAddressStore,
  (addressStore): APIAddressHash => addressStore.apiAddresses,
);

export const getAddressBookInstances = createSelector(
  getAddressStore,
  (addressStore): AddressBookInstances => addressStore.addressBookInstances,
);

export const getIsAddressBookInitialized = createSelector(
  getAddressBookInstances,
  getAddressBookNameProp,
  (addressBookInstances, addressBookName): boolean =>
    addressBookInstances[addressBookName] && addressBookInstances[addressBookName].initialized,
);

const getSelectedAddresses = createSelector(
  getAddressStore,
  getAddressBookNameProp,
  (addressStore, addressBookName): AddressHash =>
    addressStore.addressBookInstances[addressBookName]
      ? addressStore.addressBookInstances[addressBookName].selectedAddresses
      : ({} as AddressHash),
);

export const getSelectedAddress = createSelector(
  getAddresses,
  getSelectedAddresses,
  getAddressKeyProp,
  (addresses, selectedAddresses, addressKey): Maybe<APIAddress> => {
    const selectedAddress = selectedAddresses[addressKey];
    // TODO: Change return to Maybe<APIAddress | BaseAddress>
    switch (typeof selectedAddress) {
      case 'number':
        return addresses.hasOwnProperty(selectedAddress) ? addresses[selectedAddress] : null;
      default:
        return selectedAddress as APIAddress;
    }
  },
);

export const getSelectedAddressesData = createSelector(
  getAddresses,
  getSelectedAddresses,
  (addresses, selectedAddresses) =>
    Object.keys(selectedAddresses).reduce<AddressHash>((obj, key) => {
      const selectedAddress = selectedAddresses[key];
      switch (typeof selectedAddress) {
        case 'number':
          return { ...obj, [key]: addresses[selectedAddress] };
        default:
          return { ...obj, [key]: selectedAddress };
      }
    }, {}),
);

export const getIsSelectedAddressInSavedAddresses = createSelector(
  getAddresses,
  getSelectedAddress,
  (savedAddresses, selectedAddress): boolean => {
    if (!selectedAddress) return false;

    return !!Object.values(savedAddresses).find((address) =>
      areAddressesFullyEqual(address, selectedAddress),
    );
  },
);

export const getIsUpdatingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isUpdatingApiAddress,
);

export const getIsCreatingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isCreatingApiAddress,
);

export const getIsDeletingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isDeletingApiAddress,
);

export const getAddressErrorMessage = createSelector(
  getAddressStore,
  (addressStore): string => addressStore.errorMessage,
);

export const getIsLoadingApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isLoadingApiAddresses,
);

export const getIsLoadedApiAddress = createSelector(
  getAddressStore,
  (addressStore): boolean => addressStore.isLoadedApiAddresses,
);

export const getDefaultSenderAddress = createSelector(
  getAddressStore,
  (addressStore): SavedAPIAddress | null => {
    const defaultAddresses = Object.keys(addressStore.apiAddresses)
      .map((key) => addressStore.apiAddresses[key])
      .filter((address) => address.isDefaultSender);
    return defaultAddresses.length ? defaultAddresses[0] : null;
  },
);
